import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Divider,
  Paper,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  DigitalCase,
  DigitalGroundTruth,
  DigitalQuestion,
  DigitalSubmission,
  QuestionCaseSetting,
  QuestionTypeV2,
} from '../../redux/types';
import GenericDialog from '../GenericDialog';
import Prompt from '../layout/Prompt';
import { Colors } from '../utilities/Colors';
import Legend from '../digitalTrial/Legend';
import { getHighestGroundTruthScore } from '../utilities/DigitalGroundTruthUtil';
import GTScores from '../groundtruth/GTScores';

type CellBuffer = {
  submission?: DigitalSubmission;
  casee: DigitalCase;
  question: DigitalQuestion;
  answer?: string | number | JSX.Element | (JSX.Element | undefined)[] | null;
  existingGTRecord?: DigitalGroundTruth;
}

type Props = {
  digitalQuestions: DigitalQuestion[];
  digitalCases: DigitalCase[];
  submissionsMapByQuestionAndCaseId: Record<number, Record<number, DigitalSubmission> | undefined>;
  onSave?: (submission: DigitalSubmission) => void;
  settingsDictionary?: { get: (qId: number, cId: number) => QuestionCaseSetting | undefined };
  showLegend?: boolean;
  viewOnly?: boolean;
  DGTDictionaryByQuestionAndCase?: Record<number, Record<number, DigitalGroundTruth> | undefined>;
  DialogRenderElement?: React.ReactNode;
  mode: 'review'|'auswertung';
};

function getAnswerStatusColor(status?: DigitalSubmission['scoreStatus'], reviewScore?: number) {
  let ansStatus: string|undefined = status;
  if (reviewScore !== undefined && reviewScore !== null) {
    ansStatus = 'reviewed';
  }
  switch (ansStatus) {
    case 'reviewed':
      return { color: Colors.reviewed };
    case 'correct':
      return { color: Colors.correct };
    case 'acceptable':
      return { color: Colors.acceptable };
    case 'wrong':
      return { color: Colors.wrong };
    default:
      return undefined;
  }
}

const ReviewSubmissionTable: React.FC<Props> = ({
  digitalQuestions,
  digitalCases,
  submissionsMapByQuestionAndCaseId,
  onSave,
  settingsDictionary,
  showLegend,
  viewOnly,
  DGTDictionaryByQuestionAndCase,
  DialogRenderElement,
  mode,
}: Props) => {
  const [cellBuffer, setCellBuffer] = useState<CellBuffer>();
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [warningMaxScore, setWarningMaxScore] = useState<string>();
  const [disableSaveButton, setDisableSaveButton] = useState(false);
  const [showNoGTWarning, setShowNoGTWarning] = useState(false);
  const [maxScore, setMaxScore] = useState<number>();

  const scoreChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (warningMaxScore) {
      setWarningMaxScore(undefined);
    }
    const { value } = e.target;
    const { submission } = cellBuffer || {};
    const { score } = submission || {};
    let nValue = Number(value);
    if (Number.isNaN(nValue)) {
      return;
    }
    if (score !== undefined && score === Number(value)) {
      setDisableSaveButton(true);
    } else {
      setDisableSaveButton(false);
    }
    if (nValue < 0) {
      nValue = 0;
    }

    if (maxScore) {
      if (nValue > maxScore) {
        nValue = maxScore;
        setWarningMaxScore(`Max score for this question is ${maxScore}`);
        return;
      }
    }

    if (cellBuffer?.submission) {
      setCellBuffer({
        ...cellBuffer,
        submission: {
          ...cellBuffer.submission,
          reviewScore: (nValue),
        },
      });
    }
  };

  const cellClickHandler = (c: CellBuffer) => {
    if (viewOnly || !c.submission) {
      return;
    }
    if (!c.existingGTRecord) {
      setShowNoGTWarning(true);
      return;
    }
    setMaxScore(getHighestGroundTruthScore(c.existingGTRecord));
    if (warningMaxScore) {
      setWarningMaxScore(undefined);
    }
    setCellBuffer(c);
  };

  const onSaveHandler = () => {
    if (!onSave || viewOnly) {
      return;
    }
    const updatedSubmission = cellBuffer?.submission;
    if (updatedSubmission) {
      onSave(updatedSubmission);
    }
    setCellBuffer(undefined);
  };

  const onResetHandler = () => {
    if (!onSave || viewOnly) {
      return;
    }
    const updatedSubmission = cellBuffer?.submission;
    if (updatedSubmission) {
      onSave({
        ...updatedSubmission,
        reviewScore: undefined,
      });
    }
    setCellBuffer(undefined);
  };

  if (!digitalQuestions.length || !digitalCases) {
    return null;
  }

  return (
    <Box>
      <TableContainer>
        <Table sx={{ position: 'relative', overflow: 'auto' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  position: 'sticky',
                  backgroundColor: 'white',
                  left: 0,
                  width: 300,
                }}
              >
                <FormattedMessage id="review.questionHeadline" />
              </TableCell>
              {
                digitalCases.map((dc) => (
                  <TableCell key={dc.id} align="center">{dc.name}</TableCell>
                ))
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              digitalQuestions.map((dq) => (
                <TableRow key={dq.id}>
                  <TableCell
                    sx={{
                      position: 'sticky',
                      backgroundColor: 'white',
                      left: 0,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{dq.text}</Typography>
                  </TableCell>
                  {
                    digitalCases.map((dc) => {
                      const dismissed = settingsDictionary?.get(dq.id, dc.id)?.hide;
                      if (dismissed) {
                        return <TableCell sx={{ bgcolor: Colors.dismissed }} key={dc.id} align="center" />;
                      }

                      const submissionObj = submissionsMapByQuestionAndCaseId[
                        dq.id]?.[dc.id];
                      const existingGTRecord = DGTDictionaryByQuestionAndCase?.[dq.id]?.[dc.id];
                      let answer: string | number | JSX.Element |
                        null | undefined | (JSX.Element | undefined)[];
                      if (dq.type === QuestionTypeV2.NumberRange
                        && submissionObj?.type === QuestionTypeV2.NumberRange) {
                        answer = submissionObj.answer;
                      }
                      if (dq.type === QuestionTypeV2.SingleChoice
                        && submissionObj?.type === QuestionTypeV2.SingleChoice) {
                        const optionObj = dq.payload?.find((v) => v.id === submissionObj.answer);
                        answer = optionObj?.text;
                      }
                      if (dq.type === QuestionTypeV2.MultiChoice
                        && submissionObj?.type === QuestionTypeV2.MultiChoice) {
                        answer = dq.payload?.map((p) => (submissionObj.answer[p.id]
                          ? <li key={p.id}>{p.text}</li> : undefined))
                          .filter((a) => !!a);
                        answer = <ul style={{ listStylePosition: 'inside', padding: 0 }}>{answer}</ul>;
                      }
                      if (dq.type === QuestionTypeV2.Ratio
                        && submissionObj?.type === QuestionTypeV2.Ratio) {
                        answer = submissionObj.answer.join(' : ');
                      }
                      if (dq.type === QuestionTypeV2.String
                        && submissionObj?.type === QuestionTypeV2.String) {
                        answer = submissionObj.answer.answer;
                      }
                      if (dq.type === QuestionTypeV2.SingleChoiceWithText
                        && submissionObj?.type === QuestionTypeV2.SingleChoiceWithText) {
                        const optionObj = dq.payload?.find((v) => v.id in submissionObj.answer
                          && submissionObj.answer[v.id].selected);
                        if (optionObj) {
                          const val = submissionObj.answer[optionObj.id]?.text;
                          answer = `${optionObj?.text} ${val ? `(${val})` : ''}`;
                        }
                      }
                      if (dq.type === QuestionTypeV2.MultiChoiceWithText
                        && submissionObj?.type === QuestionTypeV2.MultiChoiceWithText) {
                        answer = dq.payload?.map((p) => (submissionObj.answer[p.id]?.selected
                          ? (
                            <li key={p.id}>
                              {p.text}
                              {submissionObj.answer[p.id].text ? ` (${submissionObj.answer[p.id].text})` : ''}
                            </li>
                          ) : undefined))
                          .filter((a) => !!a);
                        answer = <ul style={{ listStylePosition: 'inside', padding: 0 }}>{answer}</ul>;
                      }
                      return (
                        <TableCell
                          key={dc.id}
                          align="center"
                          sx={!viewOnly ? {
                            lineBreak: 'strict',
                            minWidth: 155,
                            '&:hover': {
                              backgroundColor: 'ButtonFace',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease',
                            },
                          } : {
                            lineBreak: 'strict',
                            minWidth: 155,
                          }}
                          onClick={() => cellClickHandler({
                            casee: dc,
                            question: dq,
                            submission: submissionObj,
                            existingGTRecord,
                            answer,
                          })}
                        >
                          <div
                            style={
                              showLegend
                                ? getAnswerStatusColor(
                                  submissionObj?.scoreStatus, submissionObj?.reviewScore,
                                )
                                : undefined
                            }
                          >
                            <Box sx={{ whiteSpace: 'pre-wrap' }}>
                              {answer === undefined
                                ? <p>-</p>
                                : answer}
                            </Box>
                          </div>
                          {
                            submissionObj && (
                              <Box display="flex" justifyContent="center" gap={0.5}>
                                {
                                  submissionObj.score != null && (
                                    <Typography
                                      variant="body2"
                                      sx={submissionObj.reviewScore != null ? {
                                        textDecoration: 'line-through',
                                        color: 'grey.400',
                                      } : null}
                                    >
                                      (
                                      {submissionObj?.score}
                                      )
                                    </Typography>
                                  )
                                }
                                {
                                  submissionObj.reviewScore != null && (
                                    <Typography variant="body2">
                                      (
                                      {submissionObj.reviewScore}
                                      )
                                    </Typography>
                                  )
                                }
                              </Box>
                            )
                          }
                        </TableCell>
                      );
                    })
                  }
                </TableRow>
              ))
            }
          </TableBody>
        </Table>
      </TableContainer>
      {
        showLegend && <Legend />
      }
      <GenericDialog
        maxWidth="md"
        isOpen={!!cellBuffer}
        onRequestClose={() => {
          setCellBuffer(undefined);
        }}
      >
        <Box sx={{ m: 1 }}>
          <Box sx={{ m: 1 }}>
            {/* heading */}
            <Box sx={{ mb: 3 }} display="flex" alignItems="center" gap={2}>
              <Typography color="primary.dark" variant="h4">{cellBuffer?.casee.name}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{cellBuffer?.question.text}</Typography>
              {/* <Chip size="small" label={cellBuffer?.question.type} /> */}
            </Box>
            {/* container 1 — minWidth:0 + flex lets long pre-wrap text shrink inside the dialog */}
            <Box display="flex" justifyContent="space-evenly" sx={{ my: 2, minWidth: 0 }}>
              <Box sx={{ p: 2, flex: '1 1 0', minWidth: 0 }}>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  <FormattedMessage id="review.gtAnswer" />
                </Typography>
                {
                  cellBuffer && (
                    <Typography
                      component="div"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        minWidth: 0,
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                      }}
                    >
                      <GTScores
                        question={cellBuffer.question}
                        groundTruth={cellBuffer.existingGTRecord}
                      />
                    </Typography>
                  )
                }
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ p: 2, flex: '1 1 0', minWidth: 0 }}>
                <Typography color="text.secondary" sx={{ mb: 1 }}>
                  <FormattedMessage id="review.submittedAnswer" />
                </Typography>
                <Typography
                  component="div"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    minWidth: 0,
                    overflowWrap: 'break-word',
                    wordBreak: 'break-word',
                  }}
                >
                  {cellBuffer?.answer}
                </Typography>
              </Box>
            </Box>
            {/* container 2 */}
            <Paper
              variant="outlined"
              sx={{
                bgcolor: 'grey.100',
                my: 2,
                display: 'flex',
                justifyContent: 'space-evenly',
                alignItems: 'flex-end',
                p: 2,
              }}
            >
              <Typography sx={{ mt: 2 }}>
                <FormattedMessage id="review.obtainedScoreDialog" />
                {`: ${cellBuffer?.submission?.score} / ${maxScore}`}
              </Typography>
              {
                mode === 'review' && (
                  <TextField
                    type="number"
                    variant="standard"
                    size="small"
                    label={<FormattedMessage id="review.reviewScoreDialog" />}
                    value={cellBuffer?.submission?.reviewScore == null ? '' : cellBuffer?.submission.reviewScore}
                    onChange={scoreChangeHandler}
                    error={!!warningMaxScore}
                    helperText={warningMaxScore}
                  />
                )
              }
              {
                mode === 'auswertung' && (
                  <Typography sx={{ mt: 2 }}>
                    <FormattedMessage id="review.reviewScoreDialog" />
                    {`: ${cellBuffer?.submission?.reviewScore == null ? 'N/A' : cellBuffer?.submission.reviewScore}`}
                  </Typography>
                )
              }
            </Paper>
            {/* Container 3 */}
            <Box>
              {
                DialogRenderElement
              }
            </Box>
          </Box>
          {/* footer */}
          {
            mode === 'review' && (
              <Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Button
                    onClick={() => setShowWarning(true)}
                    color="warning"
                    disabled={cellBuffer?.submission?.reviewScore === null}
                  >
                    <FormattedMessage id="review.resetReview" />
                  </Button>
                  <Button
                    onClick={onSaveHandler}
                    variant="contained"
                    disabled={
                      cellBuffer?.submission?.reviewScore === null
                      || disableSaveButton
                    }
                  >
                    <FormattedMessage id="save" />
                  </Button>
                </Box>
              </Box>
            )
          }
        </Box>
      </GenericDialog>
      <Prompt
        display={showNoGTWarning}
        action={() => setShowNoGTWarning(false)}
        title="No Ground Truth"
        alert
        version={2}
      >
        <Typography>No Ground Truth is set for this case and question</Typography>
      </Prompt>
      <Prompt
        display={showWarning}
        title={<FormattedMessage id="review.title" />}
        action={async (res: boolean) => {
          if (res) {
            onResetHandler();
          }
          setShowWarning(false);
        }}
        version={2}
      >
        <Typography><FormattedMessage id="review.message" /></Typography>
      </Prompt>
    </Box>
  );
};

export default ReviewSubmissionTable;
